// lista dostaw (local override – wyświetlanie order_products_count, śledzenie)
// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

import { reduxActions, useDispatch } from 'store';
import { useGetDeliveries } from 'api';
import { IDeliveryListItem, IDeliveriesRequest } from 'api/types';

/** Rozszerzenie dla API zwracającego carrier_name, tracking_number, tracking_url, status, delivery_method_name (tylko local) */
type IDeliveryListItemWithTracking = IDeliveryListItem & {
  carrier_name?: string;
  tracking_number?: string;
  tracking_url?: string;
  delivery_method_name?: string;
  order_products_count?: number;
  order_status_id?: number;
  status?: { label: string; color?: string; icon?: string };
  shipping_date_formatted?: string;
};
import {
  SearchInput,
  PageTitle,
  Button,
  DateRangePicker,
  Link,
  Select,
  Status
} from 'components/controls';
import Table, { IColumn } from 'components/controls/Table';

import styles from 'theme/pages/Deliveries/Deliveries.module.scss';
import classnames from 'classnames';

const Deliveries = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const getPosition = (string: string, subString: string, index: number) => {
    return string.split(subString, index).join(subString).length;
  };

  const urlPrefix = pathname.slice(0, getPosition(pathname, '/', 2));

  const defaultQueryParams = {
    page: 1,
    limit: 20,
    order_status_id: undefined
  } as IDeliveriesRequest & { order_status_id?: number };

  const [queryParams, setQueryParams] = useState<IDeliveriesRequest & { order_status_id?: number }>(
    defaultQueryParams
  );

  const { data: deliveriesData } = useGetDeliveries(queryParams as IDeliveriesRequest);

  useEffect(() => {
    dispatch(
      reduxActions.setBreadcrumbs([
        { name: t('Dashboard'), path: '/dashboard' },
        { name: t('Lista dostaw') }
      ])
    );
  }, []);

  const resetFilters = () => setQueryParams(defaultQueryParams);

  const areFiltersApplied =
    !!queryParams.search_keyword ||
    !!queryParams.date_from ||
    !!queryParams.date_to ||
    !!queryParams.order_status_id;

  // opcje statusów (z danych listy dostaw)
  const statusOptions = useMemo(() => {
    const unique = new Map<number, { value: number; label: string; item: { id: number; name: string } }>();
    ((deliveriesData?.items || []) as IDeliveryListItemWithTracking[]).forEach((it) => {
      if (it?.order_status_id != null && it?.status?.label) {
        unique.set(it.order_status_id, {
          value: it.order_status_id,
          label: it.status.label,
          item: { id: it.order_status_id, name: it.status.label }
        });
      }
    });
    return Array.from(unique.values());
  }, [deliveriesData?.items]);

  // Kolumny jak na dashboardzie (Symbol, Data wysyłki, Sposób dostawy, List przewozowy, Status)
  const columns: IColumn<IDeliveryListItemWithTracking>[] = useMemo(
    () => [
      {
        title: <Trans>Symbol</Trans>,
        dataIndex: 'symbol',
        align: 'left',
        renderCell: (item) =>
          item.symbol ? (
            <Link to={`/dashboard/orders/${item.order_id}`} className={styles.primaryText}>
              {item.symbol}
            </Link>
          ) : (
            '–'
          )
      },
      {
        title: <Trans>Data wysyłki</Trans>,
        dataIndex: 'shipping_date_formatted',
        align: 'left',
        renderCell: (item) => item.shipping_date_formatted ?? '–'
      },
      {
        title: <Trans>Sposób dostawy</Trans>,
        dataIndex: 'delivery_method_name',
        align: 'left',
        renderCell: (item) => item.delivery_method_name ?? '-'
      },
      {
        title: <Trans>List przewozowy</Trans>,
        dataIndex: 'tracking_number',
        align: 'left',
        renderCell: (item) => {
          const hasTracking =
            item.carrier_name || item.tracking_number || item.tracking_url;
          if (!hasTracking) return '–';
          return (
            <div className={styles.listPrzewozowyCell}>
              {(item.carrier_name || item.tracking_number) && (
                <span>
                  {item.carrier_name && item.tracking_number
                    ? `${item.carrier_name}: ${item.tracking_number}`
                    : item.carrier_name || item.tracking_number}
                </span>
              )}
              {item.tracking_url && (
                <>
                  {(item.carrier_name || item.tracking_number) && <br />}
                  <a
                    href={item.tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.trackButton}
                  >
                    <Trans>Śledź przesyłkę</Trans>
                  </a>
                </>
              )}
            </div>
          );
        }
      },
      {
        title: <Trans>Status</Trans>,
        dataIndex: 'status',
        align: 'center',
        renderCell: (item) =>
          item.status ? (
            <Status
              icon={item.status.icon ?? ''}
              message={item.status.label}
              color={item.status.color}
            />
          ) : (
            '-'
          )
      }
    ],
    []
  );

  return (
    <div className={classnames(styles.componentWrapper, 'StylePath-Pages-Deliveries')}>
      <PageTitle
        title={
          <>
            <Trans>Lista dostaw</Trans>{' '}
            <span className="thin">({deliveriesData?.total_count || 0})</span>
          </>
        }
      />
      <div className={styles.filtersWrapper}>
        <SearchInput
          placeholder={t('Podaj symbol zamówienia')}
          value={queryParams.search_keyword}
          onChange={(value) =>
            setQueryParams((prevState) => ({ ...prevState, search_keyword: value, page: 1 }))
          }
        />

        <DateRangePicker
          dateRange={{
            from: queryParams.date_from,
            to: queryParams.date_to
          }}
          onChange={(range) =>
            setQueryParams((prevState) => ({
              ...prevState,
              date_from: range.from,
              date_to: range.to
            }))
          }
        />

        <Select<{ id: number; name: string }>
          variant="bordered"
          placeholder={`${t('Wybierz status')}...`}
          options={statusOptions}
          value={queryParams.order_status_id}
          onChange={(item) =>
            setQueryParams((prev) => ({ ...prev, order_status_id: item?.id ?? undefined, page: 1 }))
          }
          clearable
        />

        <Button disabled={!areFiltersApplied} onClick={resetFilters}>
          {t('Wyczyść filtrowanie')}
        </Button>
      </div>

      <Table<IDeliveryListItemWithTracking>
        columns={columns}
        dataSource={(deliveriesData?.items || []) as IDeliveryListItemWithTracking[]}
        rowKey="id"
        pagination={{
          page: queryParams.page || 1,
          pagesCount: deliveriesData?.total_pages || 1,
          onChange: (page) => setQueryParams((prevState) => ({ ...prevState, page }))
        }}
      />
    </div>
  );
};

export default Deliveries;
